import Vue from 'vue'
import axios from 'axios'

const initState = {
  currentPage: 'index',
  index: {
    agencies: null,
    queries: {
      per_page: 10,
      page: 1,
      status: 'active',
      search: null,
      sort_by: 'id',
      sort_desc: false,
    },
    total: 0,
  },
  archived: {
    agencies: null,
    queries: {
      per_page: 10,
      page: 1,
      status: 'archived',
      search: null,
      sort_by: 'id',
      sort_desc: false,
    },
    total: 0,
  },
}

export default {
  namespaced: true,

  state: Vue.util.extend({}, initState),

  mutations: {
    getAllSuccess(state, { agencies, page, queries, total }) {
      state[page] = {
        agencies: agencies,
        queries: queries,
        total: total,
      }
    },

    createSuccess(state, { agency }) {
      state.index.agencies = null
    },

    updateSuccess(state, { id, agency }) {
      state.index.agencies = null
    },

    archiveSuccess(state) {
      state.archived.agencies = null
    },

    editSettingsSuccess(state, user) {
      this.state.auth.user = user
    },

    currentPage(state, page) {
      state.currentPage = page
    },

    uploadWhitelabelFileSuccess(state, { type, path }) {
      // Vue.set: `type` is one of header_logo / login_page_logo / favicon, and a
      // whitelabel that has never had that asset does not carry the key, so a plain
      // bracket assignment is non-reactive. Symptom: the first upload of a given asset
      // showed a success toast but the topbar/sidebar logo did not change until reload;
      // replacing an existing asset worked. The same file already uses Vue.set below.
      Vue.set(this.state.auth.user.agency.whitelabel, type, path)
    },

    deleteWhitelabelFileSuccess(state, type) {
      Vue.set(this.state.auth.user.agency.whitelabel, type, null)
    },

    updateTwilioSuccess(state, twilioConfig) {
      this.state.auth.user.agency.twilioConfig = twilioConfig
    },

    updateSendgridSuccess(state, sendgridIntegration) {
      this.state.auth.user.agency.sendgridIntegration = sendgridIntegration
    },
    updateMailgunSuccess(state, mailgunIntegration) {
      this.state.auth.user.agency.mailgunIntegration = mailgunIntegration
    },

    updateSesSuccess(state, sesIntegration) {
      this.state.auth.user.agency.sesIntegration = sesIntegration
    },

    toggleIsNewSuccess(state) {
      this.state.auth.user.agency.is_new = false
    },

    resetAllSuccess(state) {
      for (let f in state) {
        Vue.set(state, f, initState[f])
      }
    },

    addSampleAccountSuccess(state) {
      this.state.auth.user.agency.has_sample = true
    },
  },

  actions: {
    resetAll({ commit }) {
      commit('resetAllSuccess')
    },

    getAll({ commit, state }, { queries, page }) {
      return new Promise((resolve, reject) => {
        axios
          .get('/agencies', { params: queries })
          .then((res) => {
            commit('getAllSuccess', {
              agencies: res.data.data,
              page: page,
              queries: queries,
              total: res.data.meta.total,
            })
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    find({ commit }, agencyId) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/agencies/${agencyId}`)
          .then((res) => {
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    create({ commit }, agency) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/agencies`, agency)
          .then((res) => {
            commit('createSuccess', { agency: res.data.data })
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    update({ commit }, agency) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/${agency.id}`, agency)
          .then((res) => {
            commit('updateSuccess', { id: agency.id, agency: res.data.data })
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    toggleIsNew({ commit }, agency) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/${agency.id}/update-partial`, {
            is_new: false,
          })
          .then((res) => {
            commit('toggleIsNewSuccess')
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    archive({ commit }, agencyId) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/agencies/${agencyId}`)
          .then((res) => {
            commit('archiveSuccess')
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    remove({ commit }, agencyId) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/agencies/${agencyId}`)
          .then((res) => {
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    restore({ commit }, agencyId) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/agencies/${agencyId}/restore`)
          .then((res) => {
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    setCurrentPage({ commit }, page) {
      commit('currentPage', page)
    },

    editSettings({ commit }, params) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/edit-settings`, params)
          .then((res) => {
            commit('editSettingsSuccess', res.data.data)
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    uploadWhitelabelFile({ commit }, params) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/agencies/whitelabel-file`, params, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
          .then((res) => {
            commit('uploadWhitelabelFileSuccess', {
              type: params.get('type'),
              path: res.data.data.path,
            })
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    deleteWhitelabelFile({ commit }, type) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`/agencies/whitelabel-file/${type}`)
          .then((res) => {
            commit('deleteWhitelabelFileSuccess', type)
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    updateTwilio({ commit }, twilio) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/twilio-config`, twilio)
          .then((res) => {
            commit('updateTwilioSuccess', res.data.data)
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    updateSendgrid({ commit }, sendgrid) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/sendgrid-config`, sendgrid)
          .then((res) => {
            commit('updateSendgridSuccess', res.data.data)
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    updateMailgun({ commit }, sendgrid) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/mailgun-config`, sendgrid)
          .then((res) => {
            commit('updateMailgunSuccess', res.data.data)
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    updateSes({ commit }, ses) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/ses-config`, ses)
          .then((res) => {
            commit('updateSesSuccess', res.data.data)
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    adminUpdateTwilio({ commit }, { agencyId, twilio }) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/${agencyId}/twilio-config`, twilio)
          .then((res) => {
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    adminUpdateSendgrid({ commit }, { agencyId, sendgrid }) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/${agencyId}/sendgrid-config`, sendgrid)
          .then((res) => {
            resolve(res.data.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    adminUpdateBilling({ commit }, { agencyId, settings }) {
      return new Promise((resolve, reject) => {
        axios
          .put(`/agencies/${agencyId}/billing-config`, settings)
          .then((res) => {
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    addSampleAccount({ commit }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`/agencies/add-sample-account`)
          .then((res) => {
            commit('addSampleAccountSuccess')
            resolve(true)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    getAnalytics({ commit, state }, queries) {
      return new Promise((resolve, reject) => {
        axios
          .get('/agencies/analytics', { params: queries })
          .then((res) => {
            resolve(res.data)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
  },

  getters: {
    total(state) {
      return state[state.currentPage].total
    },

    queries(state) {
      return state[state.currentPage].queries
    },

    all(state) {
      return state[state.currentPage].agencies
    },

    allActive(state) {
      return state.index.agencies
    },
  },
}
